import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { openDialogByParams } from '@consts/open-dialog-by-params.consts';
import { features } from '@consts/text.const';
import { SharedEffects } from '@effects/shared.effects';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedActions } from '@states/shared/shared.action-types';
import { UiSearchComponent } from 'src/app/shared/ui-kit/ui-search/ui-search.component';
import {
  PreloaderColor, RouteModalService, UiButtonComponent, UiDatasetTableComponent,
  UiFilterChipComponent, UiNoDataComponent, UiPreloaderComponent,
} from '@ui';
import { filter, tap } from 'rxjs';
import { syncQueryParam } from 'src/app/helpers/sync-query-param.helper';
import { ConfirmModalModel } from 'src/app/services/confirm-dialogs/confirm-modal.model';
import { CategoryDialogComponent, CreateCategoryDialogData } from './components/category-dialog/category-dialog.component';
import { caseCategoriesFeature } from '@states/case-category/case-category.feature';
import { AppState } from '../../../../store/app.state';
import { CaseCategoriesActions } from '@states/case-category/case-category.actions';
import { CaseCategoriesModel } from '@models/case-categories.model';
import { createCategoriesTableConfig } from './categories-table.util';
import { ConfirmDialogModel } from '@ui';
import { SharedModels } from '@share-models';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MenuActions } from '@states/menu/menu.action-types';
import { MenuKey } from '@enums/menu.enum';

@Component({
  selector: 'app-categories',
  imports: [
    TranslatePipe,
    UiButtonComponent,
    UiSearchComponent,
    UiDatasetTableComponent,
    NgTemplateOutlet,
    UiNoDataComponent,
    UiPreloaderComponent,
    UiFilterChipComponent,
    NgClass,
  ],
  templateUrl: './categories.component.html',
  styleUrl: './categories.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoriesComponent implements OnInit {
  private readonly store$: Store<AppState> = inject(Store);
  private readonly translateService = inject(TranslateService);
  private readonly sharedEffects$ = inject(SharedEffects);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(RouteModalService);

  public loaderColor = PreloaderColor;
  protected readonly noData = features.caseManagement.categories;
  protected readonly viewStates = SharedModels.ViewState;

  protected readonly selectIsLoadingSig: Signal<boolean> = this.store$.selectSignal(caseCategoriesFeature.selectIsLoading);
  protected readonly selectIsInitialLoadingSig: Signal<boolean> = this.store$.selectSignal(caseCategoriesFeature.selectIsInitialLoading);
  protected readonly selectCategoriesSig: Signal<CaseCategoriesModel.CaseCategoryDocument[]> =
    this.store$.selectSignal(caseCategoriesFeature.selectCategories);

  protected search: WritableSignal<string> = syncQueryParam<string>('search');

  public readonly tableConfig = createCategoriesTableConfig();

  public filteredCategoriesSig: Signal<CaseCategoriesModel.CategoryWithStringRetention[]> = computed(() => {
    const categories = this.selectCategoriesSig();
    const query = this.search();

    const filtered = query
      ? categories.filter((webhook) => webhook.name.includes(query))
      : categories;

    return filtered.map((category) => ({
      ...category,
      retentionPeriod: this.translateService.instant(
        CaseCategoriesModel.RetentionPeriodsMap[category.retentionPeriod]
      ),
    }))
  });

  protected readonly viewStateSig = computed(() => {
    const categories = this.filteredCategoriesSig();
    const query = this.search();
    const filtersApplied = !!query

    let viewState: SharedModels.ViewState = SharedModels.ViewState.INITIAL_EMPTY;

    if (categories.length) {
      viewState = SharedModels.ViewState.HAS_CONTENT;
    } else if (filtersApplied) {
      viewState = SharedModels.ViewState.NO_RESULTS;
    }

    return viewState;
  })

  constructor() { }

  ngOnInit(): void {
    this.store$.dispatch(CaseCategoriesActions.startLoadingCategoriesList());
    this.store$.dispatch(MenuActions.setActiveLevel2Menu({ activeLevel2: MenuKey.categories }));

    this.sharedEffects$.confirmation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        ofType(SharedActions.showConfirmModalResultConfirm),
        filter(
          (x) =>
            x.params['type'] === ConfirmModalModel.Action.removeCaseCategory,
        ),
        tap((x) => {
          this.store$.dispatch(CaseCategoriesActions.deleteCategory({ id: x.params['id'] }));
        }),
      )
      .subscribe();

    this.modalService.initModalListeners(
      [
        {
          queryParam: openDialogByParams.createCaseCategory,
          open: () => this.openCreateDialog(),
        },
        {
          queryParam: openDialogByParams.editCaseCategory,
          open: ({ caseId }: Record<string, string>) => this.openEditDialog(caseId),
          onClose: () => {
            this.modalService.removeQueryParam([
              openDialogByParams.editCaseCategory,
              'caseId',
            ]);
          },
        },
      ],
      this.destroyRef,
    );
  }

  public onRowAction(event: {
    actionId: string;
    row: CaseCategoriesModel.CategoryWithStringRetention;
  }): void {
    switch (event.actionId) {
      case 'edit':
        this.edit(event.row._id);
        break;
      case 'delete':
        this.delete(event.row._id);
        break;
    }
  }

  public openEditDialog(id: string): MatDialogRef<CategoryDialogComponent, unknown> {
    const data: CreateCategoryDialogData = {
      id: id,
    };

    return this.dialog.open(CategoryDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '548px',
      data: data,
    });
  }

  public openCreateDialog(): MatDialogRef<CategoryDialogComponent, unknown> {
    return this.dialog.open(CategoryDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '548px',
    });
  }

  public create(): void {
    this.modalService.openModal(openDialogByParams.createCaseCategory);
  }

  public edit(caseId: string) {
    this.modalService.openModal(openDialogByParams.editCaseCategory, {
      caseId: caseId,
    });
  }

  public delete(id: string): void {
    this.store$.dispatch(
      SharedActions.showConfirmModal({
        options: {
          type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
          title: this.translateService.instant('CASE_CATEGORIES.DELETE.TITLE'),
          msg: this.translateService.instant('CASE_CATEGORIES.DELETE.MESSAGE'),
          confirm: this.translateService.instant('COMMON.DELETE'),
          disableClose: true,
          params: {
            type: ConfirmModalModel.Action.removeCaseCategory,
            id: id,
          },
        },
      }),
    );
  }
}
